/* eslint-disable react/destructuring-assignment */
import type { DialogContentProps, DialogProps } from '@radix-ui/react-dialog';
import * as Radix from '@radix-ui/react-dialog';
import type { ButtonProps } from '@shared/components';
import { Button, Text } from '@shared/components';
import {
  RadixTriggerToReactAriaAdaptor,
  RadixTriggerToReactAriaAdaptorProps,
} from '@shared/utils/radixTriggerToReactAriaAdaptor';
import clsx from 'clsx';
import type { ReactElement, ReactNode } from 'react';
import { createClose } from '../../assets/icons';
import type { StyleProps } from '../../utils';
import { makeElementClassNameFactory, makeRootClassName } from '../../utils';
import type { IconData } from '../icon';

export type ModalRootProps = DialogProps &
  StyleProps & {
    /** Expects children: `Trigger` and `Content`. */
    children: ReactNode;
  };

export type ModalTriggerProps = RadixTriggerToReactAriaAdaptorProps & {
  /** The component that should cause the dialog to open on press. */
  children: ReactNode;
};

export type ModalSize =
  | 'fullscreen'
  | 'takeover'
  | 'large'
  | 'medium'
  | 'small'
  | 'responsive-takeover';

export type ModalContentProps = StyleProps &
  DialogContentProps & {
    /**
     * The size of the dialog.
     * @default "medium"
     */
    size?: ModalSize;

    /** The content of the modal. Accepts `Header`, `Footer` or any other components. */
    children: ReactNode;
  };

export type ModalHeaderProps = StyleProps & {
  /** The title of the modal. */
  title: string;

  /**
   * Whether to show a close button for the modal in the top left.
   * @default false
   */
  hasCloseButton?: boolean;

  onClose?: () => void;

  /** A subtitle for the  modal. */
  subtitle?: string;

  pageNumber?: string;

  /**
   * Optional actions to display on the right side of the modal header.
   * Accepts `Action` components.
   */
  children?: ReactElement<ModalHeaderActionProps>[];
};

export type ModalTitleProps = StyleProps & {
  /** The title of a modal laid out without `Header`. */
  children: string;
};

export type ModalHeaderActionProps = {
  /** The header action's icon. */
  icon?: IconData;

  // TODO: not sure why this is necessary, but it is
  onPress?: ButtonProps['onPress'];
};

type MaybeFooterButton =
  | ReactElement<ModalFooterButtonProps>
  | null
  | undefined
  | false;
export type ModalFooterProps = StyleProps & {
  /** Primary and secondary buttons in the footer. Accepts `FooterButton` components. */
  children: MaybeFooterButton[] | MaybeFooterButton;
};

export type ModalFooterButtonProps = Pick<
  ButtonProps,
  'children' | 'variant'
> & {
  /**
   * Whether the button is the primary one.
   * @default false
   */
  isPrimary?: boolean;

  /**
   * Whether the button closes the dialog on press.
   * @default false
   */
  isCloseButton?: boolean;

  // TODO: not sure why this is necessary, but it is
  onPress?: ButtonProps['onPress'];

  /**
   * Whether the button is disabled.
   * @default false
   */
  isDisabled?: boolean;
};

// config
// ------

const ROOT = makeRootClassName('Modal');
const el = makeElementClassNameFactory(ROOT);

const DEFAULT_CONTENT_PROPS = {
  size: 'medium',
} as const;

const DEFAULT_HEADER_PROPS = {
  hasCloseButton: false,
} as const;

const DEFAULT_FOOTER_BUTTON_PROPS = {
  isPrimary: false,
  isCloseButton: false,
  isDisabled: false,
} as const;

const CLOSE_ICON = createClose;

// components

function Root(p: ModalRootProps): ReactElement {
  const { children, ...otherProps } = p;

  return (
    <Radix.Root {...otherProps}>
      <Radix.Portal>
        <Radix.Overlay asChild>
          <div className={el`overlay`} />
        </Radix.Overlay>
      </Radix.Portal>
      {children}
    </Radix.Root>
  );
}

function Trigger({
  hasReactAriaChildren,
  children,
}: ModalTriggerProps): ReactElement {
  return (
    <Radix.Trigger asChild>
      {hasReactAriaChildren ? (
        <RadixTriggerToReactAriaAdaptor>
          {children}
        </RadixTriggerToReactAriaAdaptor>
      ) : (
        children
      )}
    </Radix.Trigger>
  );
}

function Content(props: ModalContentProps): ReactElement {
  const p = { ...DEFAULT_CONTENT_PROPS, ...props };
  const { children, className, size, ...otherProps } = p;

  return (
    <Radix.Portal>
      <Radix.Content
        {...otherProps}
        className={clsx(ROOT, className, `size-${size}`, 'overflow-auto')}
      >
        {p.children}
      </Radix.Content>
    </Radix.Portal>
  );
}

function Header(props: ModalHeaderProps): ReactElement {
  const p = { ...DEFAULT_HEADER_PROPS, ...props };

  return (
    <div className={clsx(el`header`, p.className)}>
      <div className={el`header-titles`}>
        <p className={el`header-page-number`}>{p.pageNumber}</p>
        <Radix.Title asChild>
          <p className={el`header-title`}>{p.title}</p>
        </Radix.Title>
        {p.subtitle && (
          <Radix.Description asChild>
            <p className={el`header-subtitle`}>{p.subtitle}</p>
          </Radix.Description>
        )}
      </div>
      {p.children && <div className={el`header-actions`}>{p.children}</div>}
      {p.hasCloseButton && (
        <Radix.Close asChild>
          <RadixTriggerToReactAriaAdaptor>
            <Button
              variant="secondary"
              onPress={p.onClose}
              aria-label="Close Modal"
              size="small"
              isGhost
              className={el`header-close-button`}
              icon={CLOSE_ICON}
            />
          </RadixTriggerToReactAriaAdaptor>
        </Radix.Close>
      )}
    </div>
  );
}

function Title(p: ModalTitleProps): ReactElement {
  return (
    <Radix.Title asChild>
      <Text
        as="h2"
        type="body-md"
        isHeavy
        className={clsx('text-dark', p.className)}
      >
        {p.children}
      </Text>
    </Radix.Title>
  );
}

function Action(p: ModalHeaderActionProps): ReactElement {
  return (
    <Button
      isGhost
      size="small"
      onPress={p.onPress}
      icon={p.icon}
      className={el`header-action`}
    />
  );
}

function Footer(p: ModalFooterProps): ReactElement {
  return <div className={clsx(el`footer`, p.className)}>{p.children}</div>;
}

function footerButtonVariant(
  p: ModalFooterButtonProps
): ButtonProps['variant'] {
  return p.variant ?? (p.isPrimary ? 'primary' : 'default');
}

function FooterButton(props: ModalFooterButtonProps): ReactElement {
  const p = { ...DEFAULT_FOOTER_BUTTON_PROPS, ...props };
  const variant = footerButtonVariant(p);

  return p.isCloseButton ? (
    <Radix.Close asChild>
      <RadixTriggerToReactAriaAdaptor>
        <Button variant={variant} isDisabled={p.isDisabled}>
          {p.children}
        </Button>
      </RadixTriggerToReactAriaAdaptor>
    </Radix.Close>
  ) : (
    <Button variant={variant} onPress={p.onPress} isDisabled={p.isDisabled}>
      {p.children}
    </Button>
  );
}

export default {
  Root,
  Trigger,
  Content,
  Header,
  Title,
  Action,
  Footer,
  FooterButton,
};
